import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import Home from "@/pages/Home";
import Guests from "@/pages/Guests";
import GuestDetail from "@/pages/GuestDetail";
import Members from "@/pages/Members";
import Media from "@/pages/Media";
import Anniversary from "@/pages/Anniversary";
import Nacimiento from "@/pages/Nacimiento";
import Historia from "@/pages/Historia";
import Admin from "@/pages/Admin";
import Upcoming from "@/pages/Upcoming";

// When the backend serves the frontend from the same origin (production Hostinger),
// use relative URLs. For local dev we still read REACT_APP_BACKEND_URL from .env.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;
export const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: "Inicio", end: true },
    { to: "/invitados", label: "Invitados" },
    { to: "/miembros", label: "Miembros" },
    { to: "/en-los-medios", label: "En los medios" },
    { to: "/aniversario", label: "X Aniversario" },
  ];

  return (
    <nav className={`nav-root ${scrolled ? "scrolled" : ""}`} data-testid="site-nav">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-3 md:py-4 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src="/assets/logo-ids.jpg" alt="IDS - In Dubio" className="w-12 h-12 md:w-14 md:h-14 object-contain" />
          <div className="hidden sm:block">
            <div className="logo-mark text-[1.15rem] leading-none">in dubio</div>
            <span className="logo-tag">Tertulia Social de Sevilla</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.end}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-[11px] uppercase tracking-[0.2em]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? "Cerrar" : "Menú"}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--line)] bg-[rgba(251,250,246,0.96)] backdrop-blur-xl">
          <ul className="max-w-[1600px] mx-auto px-6 py-5 flex flex-col gap-3">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.end} className={({ isActive }) => `nav-link block ${isActive ? "active" : ""}`}>
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
}

function Footer() {
  return (
    <footer className="mt-24 border-t border-[var(--line)] bg-[rgba(245,241,232,0.55)]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 grid md:grid-cols-3 gap-10">
        <div>
          <div className="logo-mark">Tertulia <em>In Dubio</em></div>
          <span className="logo-tag">Tertulia Social de Sevilla · Desde 2014</span>
          <p className="mt-6 text-[13px] leading-relaxed text-[var(--text-muted)] max-w-xs">
            Un foro trimestral donde profesionales de diversa procedencia se reúnen a debatir los problemas sociales, culturales y políticos de nuestro tiempo.
          </p>
        </div>
        <div>
          <div className="eyebrow mb-4">Navegación</div>
          <ul className="space-y-2 text-[13px]">
            <li><Link to="/invitados" className="hover:text-[var(--text-ink)]">Archivo de invitados</Link></li>
            <li><Link to="/miembros" className="hover:text-[var(--text-ink)]">Miembros</Link></li>
            <li><Link to="/en-los-medios" className="hover:text-[var(--text-ink)]">En los medios</Link></li>
            <li><Link to="/aniversario" className="hover:text-[var(--text-ink)]">X Aniversario</Link></li>
            <li><Link to="/proximas" className="hover:text-[var(--text-ink)]">Próximas tertulias</Link></li>
            <li><Link to="/historia" className="hover:text-[var(--text-ink)]">Un poco de historia</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">Sevilla · 2014–2026</div>
          <p className="text-[13px] leading-relaxed text-[var(--text-muted)]">
            «Pensamos, hablamos, dudamos. <em>In dubio, libertas.</em>»
          </p>
          <div className="mt-6 pt-5 border-t border-[var(--line)]">
            <div className="eyebrow mb-2 text-[10px]">Producción, edición y desarrollo</div>
            <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
              <a href="https://tecnoabogado.com" target="_blank" rel="noreferrer" className="font-serif text-[15px] text-[var(--text-ink)] hover:text-[var(--gold-deep)]">@tecnoabogado</a><br />
              José Luis Vilaplana<br />
              <a href="tel:+34954564602" className="hover:text-[var(--text-ink)]">954 56 46 02</a> · <a href="mailto:info@tecnoabogado.com" className="hover:text-[var(--text-ink)]">info@tecnoabogado.com</a>
            </p>
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--line)]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row justify-between gap-2 text-[11px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
          <span>© 2026 Tertulia In Dubio</span>
          <span>Archivo vivo de pensamiento</span>
          <Link to="/admin" className="hover:text-[var(--gold-deep)]" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}

function App() {
  // Ensure Emergent badge is hidden even if injected dynamically
  useEffect(() => {
    const hide = () => {
      document.querySelectorAll('#emergent-badge, a[href*="app.emergent.sh"], #made-with-emergent').forEach((el) => {
        el.style.display = 'none';
      });
    };
    hide();
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <Navigation />
        <main className="pt-16 md:pt-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/invitados" element={<Guests />} />
            <Route path="/invitados/:slug" element={<GuestDetail />} />
            <Route path="/miembros" element={<Members />} />
            <Route path="/en-los-medios" element={<Media />} />
            <Route path="/aniversario" element={<Anniversary />} />
            <Route path="/nacimiento" element={<Nacimiento />} />
            <Route path="/historia" element={<Historia />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/proximas" element={<Upcoming />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
